import React, { useState, useMemo } from 'react';

const TaxRatesTable = ({ taxRates, jurisdictions, onEdit, onDelete, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');

  const filteredAndSortedRates = React.useMemo(() => {
    let filtered = taxRates?.filter(rate => {
      const matchesSearch = rate?.taxName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           rate?.taxCode?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           rate?.jurisdictionName?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || rate?.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    filtered?.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a?.taxName?.toLowerCase();
          bValue = b?.taxName?.toLowerCase();
          break;
        case 'rate':
          aValue = a?.taxRate;
          bValue = b?.taxRate;
          break;
        case 'jurisdiction':
          aValue = a?.jurisdictionName?.toLowerCase() || '';
          bValue = b?.jurisdictionName?.toLowerCase() || '';
          break;
        case 'effective':
          aValue = new Date(a.effectiveDate);
          bValue = new Date(b.effectiveDate);
          break;
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [taxRates, searchTerm, statusFilter, sortBy, sortOrder]);

  const formatTaxRate = (rate) => {
    return `${(rate * 100)?.toFixed(2)}%`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800'
    };

    const statusLabels = {
      active: 'Active',
      inactive: 'Inactive',
      archived: 'Archived'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${statusStyles?.[status] || statusStyles?.inactive}`}>
        {statusLabels?.[status] || status}
      </span>
    );
  };

  const getJurisdictionBadge = (type) => {
    const typeStyles = {
      federal: 'bg-blue-100 text-blue-800',
      state: 'bg-purple-100 text-purple-800',
      county: 'bg-indigo-100 text-indigo-800',
      city: 'bg-pink-100 text-pink-800',
      local: 'bg-orange-100 text-orange-800',
      international: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${typeStyles?.[type] || typeStyles?.local}`}>
        {type?.charAt(0)?.toUpperCase() + type?.slice(1) || 'Unknown'}
      </span>
    );
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '⬆️' : '⬇️';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Filters and Search */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-400">🔍</span>
              </div>
              <input
                type="text"
                placeholder="Search tax rates, codes, or jurisdictions..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Tax Name</span>
                  <span>{getSortIcon('name')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('jurisdiction')}
              >
                <div className="flex items-center space-x-1">
                  <span>Jurisdiction</span>
                  <span>{getSortIcon('jurisdiction')}</span>
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('rate')}
              >
                <div className="flex items-center space-x-1">
                  <span>Tax Rate</span>
                  <span>{getSortIcon('rate')}</span>
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Applies To
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('effective')}
              >
                <div className="flex items-center space-x-1">
                  <span>Effective Date</span>
                  <span>{getSortIcon('effective')}</span>
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedRates?.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <span className="text-4xl mb-2">📊</span>
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No tax rates found</h3>
                    <p className="text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' ?'Try adjusting your filters or search terms' :'Get started by creating your first tax rate'
                      }
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAndSortedRates?.map((rate) => (
                <tr key={rate?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm font-medium text-gray-900">{rate?.taxName}</div>
                      <div className="text-sm text-gray-500">{rate?.taxCode}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="text-sm text-gray-900">{rate?.jurisdictionName || 'Unknown'}</div>
                      <div className="mt-1">
                        {getJurisdictionBadge(rate?.jurisdictionType)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatTaxRate(rate?.taxRate)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(rate?.appliesTo || [])?.map((type, index) => (
                        <span
                          key={index}
                          className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(rate.effectiveDate)?.toLocaleDateString()}
                    {rate?.expirationDate && (
                      <div className="text-xs text-gray-500">
                        Expires: {new Date(rate.expirationDate)?.toLocaleDateString()}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(rate?.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => onEdit(rate)}
                        className="text-blue-600 hover:text-blue-900 transition-colors duration-200"
                        title="Edit tax rate"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => onDelete(rate?.id)}
                        className="text-red-600 hover:text-red-900 transition-colors duration-200"
                        title="Delete tax rate"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Summary */}
      {filteredAndSortedRates?.length > 0 && (
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredAndSortedRates?.length} of {taxRates?.length} tax rates
            </div>
            <div className="text-sm text-gray-600">
              Active: {taxRates?.filter(r => r?.status === 'active')?.length} | 
              Inactive: {taxRates?.filter(r => r?.status === 'inactive')?.length} | 
              Archived: {taxRates?.filter(r => r?.status === 'archived')?.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxRatesTable;